import { BASE_URL } from "config";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import PrintIcon from "@mui/icons-material/Print";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useMaterialUIController, setDirection } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import JsBarcode from "jsbarcode";

const API_URL = BASE_URL + "/manager/orders/show/";

export default function OrderPreview() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();
  const printReportRef = useRef();
  const [controller, dispatch] = useMaterialUIController();

  useEffect(() => {
    setDirection(dispatch, "rtl");
    return () => setDirection(dispatch, "ltr");
  }, [dispatch]);

  useEffect(() => {
    // show order details
    const fetchOrder = async () => {
      setLoading(true);
      const token = localStorage.getItem("user-token");
      try {
        const res = await fetch(`${API_URL}${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        console.log("🚀 ~ fetchOrder ~ data:", data);
        setOrder(data.data);
      } catch (err) {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  useEffect(() => {
    // توليد الباركود عند توفر order.id
    if (order && order.id && printReportRef.current) {
      try {
        JsBarcode("#order-barcode-print", String(order.id), {
          format: "CODE128",
          width: 2,
          height: 40,
          displayValue: true,
        });
      } catch (e) {}
    }
  }, [order]);

  const handlePrint = () => {
    if (!printReportRef.current) return;
    const printContents = printReportRef.current.innerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html dir="rtl" lang="ar">
      <head>
        <title>تقرير الطلب</title>
        <style>
          body { font-family: 'Cairo', Tahoma, Arial, sans-serif; background: #fff; color: #222; }
          .print-header { background: linear-gradient(90deg, #ca9937, #e0b15a); color: white; padding: 24px 0; text-align: center; border-radius: 0 0 24px 24px; }
          .section { margin: 32px 0; }
          .section-title { color: #ca9937; font-size: 1.2rem; font-weight: bold; margin-bottom: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { border: 1px solid #e0b15a; padding: 8px 12px; text-align: center; }
          th { background: #f3e3c1; color: #ca9937; }
          .summary { background: #f3e3c1; color: #ca9937; font-weight: bold; }
        </style>
      </head>
      <body>
        ${printContents}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} sx={{ color: "#ca9937" }} />
      </Box>
    );
  }

  if (!order) {
    return (
      <MDBox p={4} textAlign="center">
        <MDTypography variant="h4" color="error.main">
          تعذر تحميل بيانات الطلب
        </MDTypography>
      </MDBox>
    );
  }

  // استخراج بيانات المنتجات
  const items = order.order_store?.[0]?.items || [];

  return (
    <DashboardLayout>
      <MDBox py={4} px={2}>
        <Card sx={{ p: 4, borderRight: "6px solid #ca9937", borderRadius: 4 }}>
          {/* واجهة المستخدم العادية كما كانت */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ color: "#ca9937" }}>
              تفاصيل الطلب رقم #{order.id}
            </MDTypography>
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              طباعة الطلب
            </MDButton>
          </Box>
          {/* بيانات الطلب */}
          <MDTypography variant="h6" sx={{ color: "#e0b15a", mb: 2, mt: 2 }}>
            بيانات الطلب
          </MDTypography>
          <Box display="flex" flexWrap="wrap" gap={4} mb={2}>
            <Box>
              <MDTypography variant="body1">
                رقم الطلب: <b>{order.id}</b>
              </MDTypography>
              <MDTypography variant="body1">
                الحالة: <b>{order.status}</b>
              </MDTypography>
              <MDTypography variant="body1">
                تاريخ الإنشاء: <b>{order.created_at?.slice(0, 10)}</b>
              </MDTypography>
              <MDTypography variant="body1">
                الإجمالي:{" "}
                <b>
                  {order.total} {order.currency?.code}
                </b>
              </MDTypography>
            </Box>
            <Box>
              <MDTypography variant="body1">
                طريقة الدفع: <b>{order.payment_method?.name}</b>
              </MDTypography>
              <MDTypography variant="body1">
                حالة الدفع: <b>{order.is_paid ? "مدفوع" : "غير مدفوع"}</b>
              </MDTypography>
              <MDTypography variant="body1">
                طريقة الشحن: <b>{order.shipping_method?.name}</b>
              </MDTypography>
              <MDTypography variant="body1">
                تكلفة الشحن:{" "}
                <b>
                  {order.shipping_cost} {order.currency?.code}
                </b>
              </MDTypography>
            </Box>
          </Box>
          {/* بيانات العميل */}
          <MDTypography variant="h6" sx={{ color: "#e0b15a", mb: 2, mt: 2 }}>
            بيانات العميل
          </MDTypography>
          <Box display="flex" flexWrap="wrap" gap={4} mb={2}>
            <Box>
              <MDTypography variant="body1">
                اسم العميل: <b>{order.user?.name}</b>
              </MDTypography>
              <MDTypography variant="body1">
                رقم الجوال: <b>{order.customer_addresse?.phone}</b>
              </MDTypography>
              <MDTypography variant="body1">
                العنوان: <b>{order.customer_addresse?.address}</b>
              </MDTypography>
              <MDTypography variant="body1">
                المدينة: <b>{order.customer_addresse?.location_hierarchy?.province?.name}</b>
              </MDTypography>
              <MDTypography variant="body1">
                المنطقة: <b>{order.customer_addresse?.location_hierarchy?.state?.name}</b>
              </MDTypography>
              <MDTypography variant="body1">
                الدولة: <b>{order.customer_addresse?.location_hierarchy?.country?.name}</b>
              </MDTypography>
            </Box>
          </Box>
          {/* جدول المنتجات */}
          <MDTypography variant="h6" sx={{ color: "#e0b15a", mb: 2, mt: 2 }}>
            منتجات الطلب
          </MDTypography>
          <div
            style={{ overflowX: "auto", marginTop: 16, borderRadius: 8, border: "1px solid #eee" }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead style={{ background: "#f3e3c1" }}>
                <tr>
                  <th
                    style={{
                      padding: 10,
                      color: "#ca9937",
                      fontWeight: "bold",
                      border: "1px solid #e0b15a",
                    }}
                  >
                    الصورة
                  </th>
                  <th
                    style={{
                      padding: 10,
                      color: "#ca9937",
                      fontWeight: "bold",
                      border: "1px solid #e0b15a",
                    }}
                  >
                    اسم المنتج
                  </th>
                  <th
                    style={{
                      padding: 10,
                      color: "#ca9937",
                      fontWeight: "bold",
                      border: "1px solid #e0b15a",
                    }}
                  >
                    الكمية
                  </th>
                  <th
                    style={{
                      padding: 10,
                      color: "#ca9937",
                      fontWeight: "bold",
                      border: "1px solid #e0b15a",
                    }}
                  >
                    السعر
                  </th>
                  <th
                    style={{
                      padding: 10,
                      color: "#ca9937",
                      fontWeight: "bold",
                      border: "1px solid #e0b15a",
                    }}
                  >
                    المتغيرات
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f9f6f1" }}>
                    <td style={{ textAlign: "center", border: "1px solid #e0b15a", padding: 8 }}>
                      <img
                        src={item.product_variant?.image || item.product?.medias?.[0]?.file_name}
                        alt={item.product?.name}
                        style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover" }}
                      />
                    </td>
                    <td
                      style={{
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        border: "1px solid #e0b15a",
                        padding: 8,
                      }}
                      title={item.product?.name}
                    >
                      {item.product?.name}
                    </td>
                    <td style={{ border: "1px solid #e0b15a", padding: 8 }}>{item.quantity}</td>
                    <td style={{ border: "1px solid #e0b15a", padding: 8 }}>
                      {item.price} {order.currency?.code}
                    </td>
                    <td style={{ border: "1px solid #e0b15a", padding: 8 }}>
                      {(item.product_variant?.product_attributes || []).map((attr, i) => (
                        <span key={i} style={{ marginLeft: 8 }}>
                          {attr.attribute_value?.attribute?.name}: {attr.attribute_value?.value}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* عنصر التقرير المطبوع فقط */}
          <div style={{ display: "none" }} ref={printReportRef} id="print-report">
            <div className="print-header">
              <h2>تقرير تفاصيل الطلب</h2>
              <svg id="order-barcode-print"></svg>
            </div>
            <div className="section">
              <div className="section-title">بيانات الطلب</div>
              <div style={{ marginBottom: 12 }}>
                <div>
                  رقم الطلب: <b>{order.id}</b>
                </div>
                <div>
                  الحالة: <b>{order.status}</b>
                </div>
                <div>
                  تاريخ الإنشاء: <b>{order.created_at?.slice(0, 10)}</b>
                </div>
                <div>
                  الإجمالي:{" "}
                  <b>
                    {order.total} {order.currency?.code}
                  </b>
                </div>
                <div>
                  طريقة الدفع: <b>{order.payment_method?.name}</b>
                </div>
                <div>
                  حالة الدفع: <b>{order.is_paid ? "مدفوع" : "غير مدفوع"}</b>
                </div>
                <div>
                  طريقة الشحن: <b>{order.shipping_method?.name}</b>
                </div>
                <div>
                  تكلفة الشحن:{" "}
                  <b>
                    {order.shipping_cost} {order.currency?.code}
                  </b>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title">بيانات العميل</div>
              <div style={{ marginBottom: 12 }}>
                <div>
                  اسم العميل: <b>{order.user?.name}</b>
                </div>
                <div>
                  رقم الجوال: <b>{order.customer_addresse?.phone}</b>
                </div>
                <div>
                  العنوان: <b>{order.customer_addresse?.address}</b>
                </div>
                <div>
                  المدينة: <b>{order.customer_addresse?.location_hierarchy?.province?.name}</b>
                </div>
                <div>
                  المنطقة: <b>{order.customer_addresse?.location_hierarchy?.state?.name}</b>
                </div>
                <div>
                  الدولة: <b>{order.customer_addresse?.location_hierarchy?.country?.name}</b>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-title">منتجات الطلب</div>
              <table>
                <thead>
                  <tr>
                    <th>الصورة</th>
                    <th>اسم المنتج</th>
                    <th>الكمية</th>
                    <th>السعر</th>
                    <th>المتغيرات</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <img
                          src={item.product_variant?.image || item.product?.medias?.[0]?.file_name}
                          alt={item.product?.name}
                          style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover" }}
                        />
                      </td>
                      <td>{item.product?.name}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {item.price} {order.currency?.code}
                      </td>
                      <td>
                        {(item.product_variant?.product_attributes || []).map((attr, i) => (
                          <span key={i} style={{ marginLeft: 8 }}>
                            {attr.attribute_value?.attribute?.name}: {attr.attribute_value?.value}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </MDBox>
      {/* CSS للطباعة: إظهار التقرير فقط عند الطباعة */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-report, #print-report * { visibility: visible !important; }
          #print-report { position: absolute; left: 0; top: 0; width: 100vw; background: #fff; z-index: 9999; }
        }
      `}</style>
    </DashboardLayout>
  );
}
